import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage } from "../api/client";
import type { Category, Preferences, Preferred, Unit } from "../api/types";
import { Card, vibrate } from "../components/Bits";
import { HeightFields } from "../components/HeightFields";
import { parseHeight, cmToFtIn } from "../lib/units";
import { markOnboardingSkipped, useApp } from "../state";
import { ALTER_CHOICES, mergePrefItems, prefChoices, toggleAlter } from "../lib/prefs";

export function Onboarding() {
  const { me, meta, setMe } = useApp();
  const nav = useNavigate();
  const [step, setStep] = useState<"height" | "prefs">("height");
  const [unit, setUnit] = useState<Unit>(me.user.unit);
  const init = me.user.height_cm != null ? cmToFtIn(me.user.height_cm) : null;
  const [a, setA] = useState(me.user.height_cm != null ? (me.user.unit === "cm" ? String(Math.round(me.user.height_cm)) : String(init!.ft)) : "");
  const [b, setB] = useState(init && me.user.unit !== "cm" ? String(init.inch) : "");
  const [picked, setPicked] = useState<Record<string, Preferred>>({});
  const [alter, setAlter] = useState<Preferences["alter"]>({ hem: false, take_in: false, never: false });
  const [existing, setExisting] = useState<Preferences | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.preferences().then(setExisting, () => setExisting(null));
  }, []);

  const groups = useMemo(
    () =>
      (Object.keys(meta.categories) as Category[])
        .map((c) => ({ cat: c, name: meta.categories[c].name, fps: meta.categories[c].fit_points.filter((f) => f.onboarding) }))
        .filter((g) => g.fps.length),
    [meta],
  );

  const skip = async () => {
    markOnboardingSkipped();
    try {
      setMe(await api.putProfile({ unit }));
    } catch {
      /* skipping must never block */
    }
    nav("/", { replace: true });
  };

  const saveHeight = async (e: FormEvent) => {
    e.preventDefault();
    const cm = parseHeight(unit, a, b);
    if (cm == null) {
      setErr(unit === "cm" ? "Type your height in centimeters, like 168." : "Type your height in feet and inches, like 5 and 6.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      setMe(await api.putProfile({ height_cm: cm, unit }));
      if (groups.length) setStep("prefs");
      else nav("/", { replace: true });
    } catch (x) {
      setErr(errorMessage(x));
    } finally {
      setBusy(false);
    }
  };

  const savePrefs = async () => {
    setBusy(true);
    setErr("");
    const items = mergePrefItems(existing?.items ?? [], picked);
    try {
      await api.putPreferences({ items, alter });
      setMe(await api.me());
      nav("/", { replace: true });
    } catch (x) {
      setErr(errorMessage(x));
    } finally {
      setBusy(false);
    }
  };

  if (step === "height")
    return (
      <section className="view enter">
        <h1>
          <span className="ph">First, how</span> <span className="ph"><em>tall</em> are you?</span>
        </h1>
        <p className="lede">It helps us say where hems will land. You can change it any time.</p>
        <Card tag="Your height" mark="1">
          <form className="stack" onSubmit={saveHeight} noValidate>
            <HeightFields unit={unit} a={a} b={b} onUnit={setUnit} onA={setA} onB={setB} />
            {err && <p className="hint warn">{err}</p>}
            <button className="btn primary" type="submit" disabled={busy}>
              Next
            </button>
          </form>
        </Card>
        <button className="btn ghost" type="button" onClick={skip}>
          Skip for now
        </button>
      </section>
    );

  const answered = (id: string) => id in picked;
  return (
    <section className="view enter">
      <h1>
        <span className="ph">How do you</span> <span className="ph">like things to <em>fit?</em></span>
      </h1>
      <p className="lede">Three quick taps. We use them to word your notes, never to pick a size for you.</p>
      {groups.map((g, gi) => (
        <Card key={g.cat} tag={g.name.charAt(0).toUpperCase() + g.name.slice(1)} mark={gi + 2}>
          <div className="qrows">
            {g.fps.map((f) => (
              <div key={f.id} className={"qrow" + (answered(f.id) ? " answered" : "")}>
                <h4>
                  <span className="ck" aria-hidden="true" />
                  {f.onboarding!.question}
                </h4>
                <div className="row" role="group" aria-label={f.onboarding!.question}>
                  {prefChoices(f).map(([v, label]) => (
                    <button
                      key={v}
                      type="button"
                      className="chip sm"
                      aria-pressed={picked[f.id] === v}
                      onClick={() => {
                        vibrate();
                        // Tapping the picked one again clears it.
                        setPicked((p) => {
                          const { [f.id]: was, ...rest } = p;
                          return was === v ? rest : { ...rest, [f.id]: v };
                        });
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}
      <Card tag="Tailoring" mark="✂">
        <p className="aside">Would you have something altered?</p>
        <div className="row">
          {ALTER_CHOICES.map(([k, label]) => (
            <button key={k} type="button" className="chip sm" aria-pressed={alter[k]} onClick={() => setAlter((x) => toggleAlter(x, k))}>
              {label}
            </button>
          ))}
        </div>
      </Card>
      {err && <p className="hint warn">{err}</p>}
      <div className="stack">
        <button className="btn primary" type="button" onClick={savePrefs} disabled={busy}>
          Start checking
        </button>
        <button className="btn ghost" type="button" onClick={skip}>
          Skip for now
        </button>
      </div>
    </section>
  );
}
